import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Code2,
  FileJson2,
  KeyRound,
  ShieldCheck,
  TerminalSquare,
  Zap,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { SEO } from '../shared/components/SEO';

interface DevelopersPageProps {
  onNavigate?: (view: string) => void;
}

type SnippetTab = 'curl' | 'javascript' | 'python' | 'mcp';

const ENDPOINTS = [
  {
    method: 'POST',
    path: '/api-optimize-resume',
    scope: 'optimize',
    body: '{ resume, job_description }',
    returns: '{ optimized_resume, match_score, changes_summary }',
  },
  {
    method: 'POST',
    path: '/api-score-resume',
    scope: 'score',
    body: '{ resume, job_description }',
    returns: '{ ats_score, gap_analysis, keyword_hits, keyword_misses }',
  },
  {
    method: 'POST',
    path: '/api-cover-letter',
    scope: 'cover_letter',
    body: '{ resume, job_description, tone? }',
    returns: '{ cover_letter }',
  },
  {
    method: 'POST',
    path: '/api-interview-questions',
    scope: 'questions',
    body: '{ job_description, level? }',
    returns: '{ questions: string[] }',
  },
] as const;

function resolveSupabaseFunctionsBase(): string {
  const explicitRef = import.meta.env.VITE_SUPABASE_PROJECT_REF?.trim();
  if (explicitRef && /^[a-z0-9-]{8,40}$/i.test(explicitRef)) {
    return `https://${explicitRef}.supabase.co/functions/v1`;
  }

  const candidates = [
    import.meta.env.VITE_SUPABASE_PROJECT_URL?.trim(),
    import.meta.env.VITE_SUPABASE_URL?.trim(),
  ].filter(Boolean) as string[];

  for (const raw of candidates) {
    try {
      const url = new URL(raw.replace(/\/+$/, ''));
      if (/\.supabase\.co$/i.test(url.hostname)) {
        return `${url.origin}/functions/v1`;
      }
    } catch {
      // Ignore invalid env values and continue to the documented placeholder.
    }
  }

  return 'https://YOUR_PROJECT_REF.supabase.co/functions/v1';
}

function resolveSiteOrigin(): string {
  const raw = (
    import.meta.env.VITE_PUBLIC_SITE_URL ||
    import.meta.env.VITE_SITE_URL ||
    'https://www.gograbjob.com'
  ).replace(/\/+$/, '');

  try {
    return new URL(raw).origin;
  } catch {
    return 'https://www.gograbjob.com';
  }
}

function snippetFor(tab: SnippetTab, functionsBase: string, siteOrigin: string): string {
  const optimizeUrl = `${functionsBase}/api-optimize-resume`;
  const scoreUrl = `${functionsBase}/api-score-resume`;

  switch (tab) {
    case 'curl':
      return `curl -X POST "${optimizeUrl}" \\
  -H "Authorization: Bearer ggj_YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "resume": "Jane Doe\\nSenior Frontend Engineer...",
    "job_description": "We are hiring a React engineer..."
  }'`;
    case 'javascript':
      return `const response = await fetch("${scoreUrl}", {
  method: "POST",
  headers: {
    "Authorization": "Bearer ggj_YOUR_API_KEY",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    resume: resumeText,
    job_description: jobDescription
  })
});

const result = await response.json();
console.log(result.data.ats_score, result.data.keyword_misses);`;
    case 'python':
      return `import requests

response = requests.post(
    "${functionsBase}/api-cover-letter",
    headers={
        "Authorization": "Bearer ggj_YOUR_API_KEY",
        "Content-Type": "application/json",
    },
    json={
        "resume": resume_text,
        "job_description": job_description,
        "tone": "professional",
    },
    timeout=60,
)

print(response.json()["data"]["cover_letter"])`;
    case 'mcp':
      return `{
  "mcpServers": {
    "gograbjob": {
      "url": "${siteOrigin}/mcp/tools.json",
      "headers": {
        "Authorization": "Bearer ggj_YOUR_API_KEY"
      }
    }
  }
}`;
  }
}

const DevelopersPage: React.FC<DevelopersPageProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<SnippetTab>('curl');
  const functionsBase = useMemo(resolveSupabaseFunctionsBase, []);
  const siteOrigin = useMemo(resolveSiteOrigin, []);
  const code = useMemo(
    () => snippetFor(activeTab, functionsBase, siteOrigin),
    [activeTab, functionsBase, siteOrigin],
  );

  const handleNavigate = (view: string) => {
    if (onNavigate) {
      onNavigate(view);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.location.href = view === 'home' ? '/' : `/${view}`;
  };

  const getApiKey = () => {
    try {
      sessionStorage.setItem('redirect_after_login', '/dashboard');
    } catch {
      // Best-effort only; login still works without the redirect hint.
    }
    handleNavigate('login');
  };

  return (
    <div className="developers-marketing-page min-h-screen bg-white text-gray-900 transition-colors dark:bg-gray-950 dark:text-white">
      <SEO
        title="Developer API for Resume Optimization | GoGrabJob"
        description="Build with GoGrabJob APIs for job matching, resume tailoring, ATS scoring, cover letters, and interview prep. Get API keys, OpenAPI docs, and MCP tools."
        canonical={`${siteOrigin}/developers`}
        ogImage={`${siteOrigin}/og-default.png`}
      />

      <MarketingLandingNav onNavigate={handleNavigate} variant="marketing" />

      <main className={MARKETING_NAV_TOP_OFFSET}>
        <section className="relative overflow-hidden border-b border-gray-200 bg-gradient-to-b from-purple-50 via-white to-white dark:border-white/10 dark:from-purple-950/20 dark:via-gray-950 dark:to-gray-950">
          <div className="absolute inset-x-0 top-0 h-48 bg-[radial-gradient(circle_at_top,rgba(108,71,255,0.20),transparent_60%)]" aria-hidden />
          <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-24">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white/80 px-3 py-1 text-sm font-semibold text-purple-700 shadow-sm dark:border-purple-500/30 dark:bg-purple-500/10 dark:text-purple-200">
                <TerminalSquare className="h-4 w-4" />
                Career intelligence APIs for builders
              </div>
              <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-gray-950 dark:text-white sm:text-5xl lg:text-6xl">
                Add job matching and resume-tailoring AI to your product.
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600 dark:text-gray-300">
                Use GoGrabJob&apos;s authenticated REST endpoints to rank roles against a profile,
                tailor an existing resume to a job description, score ATS fit, draft cover letters,
                and prepare likely interview questions.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={getApiKey}
                  className="home-marketing-cta rounded-xl text-sm font-bold shadow-lg"
                >
                  Get API key
                  <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href="/.well-known/openapi.yaml"
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 text-sm font-bold text-gray-900 transition hover:border-purple-300 hover:text-purple-700 dark:border-white/15 dark:bg-white/5 dark:text-white dark:hover:border-purple-400"
                >
                  View OpenAPI spec
                  <FileJson2 className="h-4 w-4" />
                </a>
              </div>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Public docs. Login is required only when you create and manage
                your personal API keys.
              </p>
            </div>

            <div className="rounded-3xl border border-gray-200 bg-gray-950 p-3 shadow-2xl shadow-purple-500/10 dark:border-white/10">
              <div className="mb-3 flex items-center gap-2 px-2 pt-1">
                <span className="h-3 w-3 rounded-full bg-red-400" />
                <span className="h-3 w-3 rounded-full bg-yellow-400" />
                <span className="h-3 w-3 rounded-full bg-green-400" />
                <span className="ml-2 text-xs text-gray-400">api-optimize-resume</span>
              </div>
              <pre className="max-h-[460px] overflow-auto rounded-2xl bg-black/60 p-4 text-xs leading-6 text-gray-100 sm:text-sm">
                <code>{snippetFor('curl', functionsBase, siteOrigin)}</code>
              </pre>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-3">
            {[
              {
                icon: KeyRound,
                title: 'Bearer API keys',
                text: 'Keys are prefixed with ggj_ and validated server-side with SHA-256 hashes.',
              },
              {
                icon: ShieldCheck,
                title: 'Scoped access',
                text: 'Each endpoint checks the required scope before any AI call runs.',
              },
              {
                icon: ClipboardList,
                title: 'Usage logging',
                text: 'Successful and failed requests are logged for analytics and rate limits.',
              },
            ].map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-200">
                  <Icon className="h-5 w-5" />
                </div>
                <h2 className="text-lg font-bold text-gray-950 dark:text-white">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-gray-50 py-14 dark:bg-gray-900/60">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.75fr_1.25fr] lg:px-8">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-purple-100 px-3 py-1 text-sm font-bold text-purple-700 dark:bg-purple-500/15 dark:text-purple-200">
                <Code2 className="h-4 w-4" />
                Quickstart
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-950 dark:text-white">
                Copy, paste, ship.
              </h2>
              <p className="mt-4 text-gray-600 dark:text-gray-300">
                These examples use the production Supabase Edge Function base URL
                when it is available from Vite env. If the page shows
                <code className="mx-1 rounded bg-gray-200 px-1 py-0.5 text-xs dark:bg-white/10">
                  YOUR_PROJECT_REF
                </code>
                replace it with your deployed GoGrabJob project ref.
              </p>
            </div>

            <div className="overflow-hidden rounded-3xl border border-gray-200 bg-gray-950 shadow-xl dark:border-white/10">
              <div className="flex flex-wrap gap-2 border-b border-white/10 bg-white/5 p-3">
                {[
                  ['curl', 'cURL'],
                  ['javascript', 'JavaScript'],
                  ['python', 'Python'],
                  ['mcp', 'MCP config'],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveTab(id as SnippetTab)}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      activeTab === id
                        ? 'bg-white text-gray-950'
                        : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <pre className="max-h-[460px] overflow-auto p-5 text-xs leading-6 text-gray-100 sm:text-sm">
                <code>{code}</code>
              </pre>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-950 dark:text-white">
                Endpoint reference
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                All endpoints accept JSON, require <code>Authorization: Bearer ggj_...</code>,
                and return <code>{'{ success, data, usage }'}</code>.
              </p>
            </div>
            <a href="/mcp/tools.json" className="text-sm font-bold text-purple-700 hover:underline dark:text-purple-300">
              View MCP tools JSON
            </a>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/5">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-left text-sm dark:divide-white/10">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-white/5 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 font-bold">Method</th>
                    <th className="px-4 py-3 font-bold">Path</th>
                    <th className="px-4 py-3 font-bold">Scope</th>
                    <th className="px-4 py-3 font-bold">Body</th>
                    <th className="px-4 py-3 font-bold">Returns</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                  {ENDPOINTS.map((endpoint) => (
                    <tr key={endpoint.path} className="align-top">
                      <td className="px-4 py-4">
                        <span className="rounded-md bg-green-100 px-2 py-1 text-xs font-bold text-green-700 dark:bg-green-500/15 dark:text-green-300">
                          {endpoint.method}
                        </span>
                      </td>
                      <td className="px-4 py-4 font-mono text-sm text-gray-950 dark:text-white">{endpoint.path}</td>
                      <td className="px-4 py-4 font-mono text-xs text-purple-700 dark:text-purple-300">{endpoint.scope}</td>
                      <td className="px-4 py-4 font-mono text-xs text-gray-600 dark:text-gray-300">{endpoint.body}</td>
                      <td className="px-4 py-4 font-mono text-xs text-gray-600 dark:text-gray-300">{endpoint.returns}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <article className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/5 sm:p-8">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-200">
                <FileJson2 className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-bold text-gray-950 dark:text-white">MCP and OpenAPI ready</h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300">
                Use the MCP tools file for agent integrations, or point any
                OpenAPI-compatible client at the published spec.
              </p>
              <div className="mt-5 space-y-2 text-sm">
                <a className="block font-mono text-purple-700 hover:underline dark:text-purple-300" href="/mcp/tools.json">
                  {siteOrigin}/mcp/tools.json
                </a>
                <a className="block font-mono text-purple-700 hover:underline dark:text-purple-300" href="/.well-known/openapi.yaml">
                  {siteOrigin}/.well-known/openapi.yaml
                </a>
              </div>
            </article>

            <article className="rounded-3xl border border-purple-200 bg-gradient-to-br from-purple-50 to-white p-6 shadow-sm dark:border-purple-500/30 dark:from-purple-950/30 dark:to-white/5 sm:p-8">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-purple-700 shadow-sm dark:bg-white/10 dark:text-purple-200">
                <Zap className="h-6 w-6" />
              </div>
              <h2 className="text-2xl font-bold text-gray-950 dark:text-white">Pricing that scales with your job hunt</h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300">
                Start with free credits, then upgrade when you need more resume
                optimizations, cover letters, scoring, and priority processing.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-gray-700 dark:text-gray-200">
                {['Free tier to test the APIs', 'Plan-backed rate limits', 'Developer settings for key management'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={getApiKey}
                  className="home-marketing-cta rounded-xl text-sm font-bold"
                >
                  Get API key
                </button>
                <button
                  type="button"
                  onClick={() => handleNavigate('pricing')}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-gray-950 hover:border-purple-300 dark:border-white/15 dark:bg-white/10 dark:text-white"
                >
                  View pricing
                </button>
              </div>
            </article>
          </div>
        </section>
      </main>

      <MarketingPageFooter onNavigate={handleNavigate} theme="developers" />
    </div>
  );
};

export default DevelopersPage;
